import {Component, OnInit} from '@angular/core';
import lodash from "lodash";
import moment from "moment";
import {RefdataUtils} from "../../refdata/refdata-utils";
import {Handler} from "../../../common/handler";
import {ChartDataPerMeasurement} from "../../../utils/chart-data-provider";
import {
    CompassRecord,
    EntityEnergyCompassComponent
} from "../../common/entity-energy-compass/entity-energy-compass.component";
import {DataType, MeasurementsResult} from "@flowmaps-com/flowmaps-typescriptmodels";
import {
    DEFAULT_WEII_CALCULATION_OPTIONS,
    WeiiCalculationOptionId,
    WeiiCalculationOptionsState
} from "../../../utils/weii-calculation.service";
import {selectWeiiYearWindow, weiiProvisionalThrough} from './weii-period.utils';

@Component({
    selector: 'app-location-energy-compass',
    templateUrl: '../../common/entity-energy-compass/entity-energy-compass.component.html',
    styleUrls: ['../../common/entity-energy-compass/entity-energy-compass.component.scss']
})
@Handler()
export class LocationEnergyCompassComponent extends EntityEnergyCompassComponent implements OnInit {
    override measurements: DataType[] = [
        DataType.electricityConsumption, DataType.electricityConsumptionOffPeak,
        DataType.electricityFeedIn, DataType.electricityFeedInOffPeak,
        DataType.electricityGrossProduction,
        DataType.gasConsumption, DataType.heatConsumption, DataType.coolingConsumption,
        DataType.chargePointConsumption, DataType.climateNormalizationDegreeDays
    ];

    override get parisProofValue(): number {
        return this.buildingType?.info.efficiencyScale["ParisProof"] || 0;
    }

    override get noDataText(): string {
        if (!this._location) {
            return 'No location selected';
        }
        if (!RefdataUtils.getLocationArea(this._location)) {
            return 'No usable floor area available.';
        }
        if (this.values.length === 0) {
            return 'No electricity consumption data available for this location';
        }
        return '';
    }

    override computeValues = (d: ChartDataPerMeasurement): CompassRecord[] => {
        const area = RefdataUtils.getLocationArea(this._location);
        if (!area || !d?.totalsPerYear) return [];
        const values = Object.entries(d.totalsPerYear.measurements)
            .flatMap(([type, points]) =>
                points.map(dp => ({
                    timeRange: dp.timeRange,
                    value: dp.value,
                    measurementType: type as DataType
                }))
            );
        const periods = selectWeiiYearWindow(
            Object.entries(lodash.groupBy(values, v => v.timeRange.start)), d.timeRange?.end);
        const standardRecords = periods.map(([start, group]) =>
            this.calculateYear(start, group, area, DEFAULT_WEII_CALCULATION_OPTIONS));
        const onsiteGenerationAvailableForAnyYear = standardRecords.some(
            record => record.calculation?.onsiteGenerationAvailable === true);
        const onsiteGenerationSelected = this.weiiCalculationService
            .isOptionEnabled(WeiiCalculationOptionId.IncludeOnsiteGeneration) && onsiteGenerationAvailableForAnyYear;
        const availabilityOptions: WeiiCalculationOptionsState = {
            ...DEFAULT_WEII_CALCULATION_OPTIONS,
            [WeiiCalculationOptionId.IncludeOnsiteGeneration]: onsiteGenerationSelected
        };
        const availabilityRecords = onsiteGenerationSelected
            ? periods.map(([start, group]) => this.calculateYear(
                start, group, area, availabilityOptions))
            : standardRecords;
        const correctionAvailableForAnyYear = availabilityRecords.some(
            record => record.calculation?.evCorrectionAvailable === true);
        const completedClimateSensitiveRecords = standardRecords.filter(record => !record.provisional)
            .filter(record => (record.calculation?.components.gasKwh || 0) > 0
                || (record.calculation?.components.heatKwh || 0) > 0);
        const climateNormalizationAvailable = !this._location.info?.allElectric
            && completedClimateSensitiveRecords.length > 0
            && completedClimateSensitiveRecords.some(
                record => record.calculation?.climateNormalizationAvailable === true);
        const selectedOptions: WeiiCalculationOptionsState = {
            ...this.weiiCalculationService.options,
            [WeiiCalculationOptionId.ExcludeEvCharging]:
                this.weiiCalculationService.isOptionEnabled(WeiiCalculationOptionId.ExcludeEvCharging)
                && correctionAvailableForAnyYear,
            [WeiiCalculationOptionId.IncludeOnsiteGeneration]: onsiteGenerationSelected,
            [WeiiCalculationOptionId.NormalizeClimateYear]:
                this.weiiCalculationService.isOptionEnabled(WeiiCalculationOptionId.NormalizeClimateYear)
                && climateNormalizationAvailable
        };
        return (Object.values(selectedOptions).some(Boolean)
            ? periods.map(([start, group], index) => this.calculateYear(
                start, group, area, standardRecords[index].calculation?.climateNormalizationAvailable
                    ? selectedOptions
                    : {...selectedOptions, [WeiiCalculationOptionId.NormalizeClimateYear]: false}))
            : standardRecords)
            .sort(RefdataUtils.locationEnergyCompassYearsComparator.compare);
    }

    private calculateYear(start: string, group: any[], area: number,
                          calculationOptions: WeiiCalculationOptionsState): CompassRecord {
        const measurements = lodash.mapValues(lodash.groupBy(group, value => value.measurementType), points =>
            points.map(point => ({timeRange: point.timeRange, value: point.value})));
        const calculation = this.weiiCalculationService.calculateMeasurements({
            measurements: [{
                entityId: this._location.locationId,
                measurements,
                estimatedMeasurements: {}
            } as MeasurementsResult],
            floorAreaM2: area,
            isFullElectric: this._location.info?.allElectric === true,
            calculationOptions,
            calculationPeriod: group[0].timeRange,
            buildingCategory: this.buildingType?.info?.name,
            parisProofThreshold: this.parisProofValue
        });
        const year = moment(start).year();
        const provisionalThrough = weiiProvisionalThrough(year);
        return {
            year,
            value: lodash.round(calculation.effectiveWeii ?? 0, 1),
            calculation,
            provisional: provisionalThrough != null,
            provisionalThrough: provisionalThrough || undefined
        };
    }
}
